import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PRIORITIES, PRIORITY_LABEL, SUPPORT_MODEL_LABEL,
  type CountryCode, type Priority, type SlaCommitment, type SlaCommitmentRules, type SlaDuration, type SlaPolicy, type SupportModelKey
} from '@veta-desk/domain';
import { api, ApiError, type SlaSettingsData } from '../lib/api';

// Configuración del SLA (admin): metas por prioridad (estándar SAP por omisión), jornada hábil,
// país por cliente (zona horaria y festivos) y días de asueto adicionales.

const input = 'h-9 rounded-md border border-[#D6D3CD] bg-white px-2 text-sm focus:border-accent focus:outline-none';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const btnSecondary = 'inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-paper disabled:opacity-50';
const th = 'border-b border-line px-3 py-2.5 font-semibold';

interface ExtraRow { country: CountryCode; date: string; name: string }

interface Form {
  policy: SlaPolicy;
  commitment: SlaCommitmentRules;
  businessHours: { startHour: number; endHour: number };
  country: CountryCode;
  orgCountries: Record<string, CountryCode>;
  extra: ExtraRow[];
}

const toForm = (d: SlaSettingsData): Form => ({
  policy: d.policy,
  commitment: d.commitment,
  businessHours: d.businessHours,
  country: d.country,
  orgCountries: d.orgCountries,
  extra: Object.entries(d.extraHolidays).flatMap(([country, list]) => (list ?? []).map(h => ({ country: country as CountryCode, ...h })))
});

const fmtDate = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

function DurationInput({ value, onChange, label }: { value: SlaDuration; onChange: (v: SlaDuration) => void; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <input aria-label={`${label}: cantidad`} type="number" min={0.25} step={0.25} className={`${input} w-20 tabular-nums`}
        value={value.value} onChange={e => onChange({ ...value, value: Number(e.target.value) })} />
      <select aria-label={`${label}: unidad`} className={input} value={value.unit} onChange={e => onChange({ ...value, unit: e.target.value as 'h' | 'd' })}>
        <option value="h">horas</option>
        <option value="d">días hábiles</option>
      </select>
    </span>
  );
}

const same = (a: SlaDuration, b: SlaDuration) => a.value === b.value && a.unit === b.unit;

export function SlaSettings() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['admin-sla'], queryFn: () => api<SlaSettingsData>('/admin/sla') });
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (query.data && !form) setForm(toForm(query.data)); }, [query.data, form]);

  const save = useMutation({
    mutationFn: (f: Form) => api<SlaSettingsData>('/admin/sla', {
      method: 'PUT',
      body: JSON.stringify({
        policy: f.policy,
        commitment: f.commitment,
        businessHours: f.businessHours,
        country: f.country,
        orgCountries: f.orgCountries,
        extraHolidays: f.extra.filter(r => r.date && r.name.trim()).reduce<Record<string, { date: string; name: string }[]>>((acc, r) => {
          (acc[r.country] ??= []).push({ date: r.date, name: r.name.trim() });
          return acc;
        }, {})
      })
    }),
    onSuccess: d => {
      setError(''); setSaved(true); setForm(toForm(d));
      queryClient.setQueryData(['admin-sla'], d);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-config'] });
    },
    onError: e => { setSaved(false); setError(e instanceof ApiError ? e.message : 'No se pudo guardar.'); }
  });

  const dirty = useMemo(() => Boolean(form && query.data && JSON.stringify(form) !== JSON.stringify(toForm(query.data))), [form, query.data]);
  if (query.isError) return <p role="alert" className="text-sm text-[#8F1D1D]">No se pudo cargar el SLA.</p>;
  if (!form || !query.data) return <p role="status" className="py-6 text-sm text-muted">Cargando…</p>;

  const data = query.data;
  const std = data.standard;
  const countryName = (c: CountryCode) => data.countries.find(x => x.code === c)?.name ?? c;
  const set = (patch: Partial<Form>) => { setSaved(false); setForm(f => f && { ...f, ...patch }); };
  const setTarget = (kind: 'response' | 'resolution', p: Priority, v: SlaDuration) =>
    set({ policy: { ...form.policy, [kind]: { ...form.policy[kind], [p]: v } } });
  const isStandard = PRIORITIES.every(p => same(form.policy.response[p], std.response[p]) && same(form.policy.resolution[p], std.resolution[p]))
    && form.policy.p1RoundTheClock === std.p1RoundTheClock;
  const hours = Array.from({ length: 25 }, (_, h) => h);

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      {error && <p role="alert" className="rounded-md bg-[#FBEAEA] px-3 py-2 text-sm text-[#8F1D1D]">{error}</p>}

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold">Metas por prioridad</h2>
          {isStandard
            ? <span className="rounded bg-accent-soft px-2 py-0.5 text-xs font-semibold text-accent">Estándar SAP</span>
            : <button type="button" className="text-sm text-accent hover:underline" onClick={() => set({ policy: std })}>Restablecer estándar SAP</button>}
        </div>
        <p className="text-sm text-muted">
          Por omisión, el estándar de soporte de SAP: P1 (Very High) reacción en 1 h y solución o plan de acción en 4 h, 24×7;
          P2 (High) reacción en 4 h hábiles. Para P3 y P4 SAP no fija meta: se usa la convención de AMS.
          Más adelante se podrán definir metas por tipo de cliente o contrato.
        </p>
        <div className="overflow-x-auto rounded-lg border border-line bg-white">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted">
                <th scope="col" className={th}>Prioridad</th>
                <th scope="col" className={th}>Primera respuesta</th>
                <th scope="col" className={th}>Resolución (INC) / cumplimiento (SOL)</th>
              </tr>
            </thead>
            <tbody>
              {PRIORITIES.map(p => (
                <tr key={p} className="border-b border-[#EFEDE9] last:border-0">
                  <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-medium">
                    {PRIORITY_LABEL[p]}
                    {p === 'P1' && form.policy.p1RoundTheClock && <span className="ml-1.5 rounded bg-[#EDEBE7] px-1.5 py-0.5 text-[10px] font-semibold text-muted">24×7</span>}
                  </th>
                  <td className="px-3 py-2"><DurationInput label={`${p} primera respuesta`} value={form.policy.response[p]} onChange={v => setTarget('response', p, v)} /></td>
                  <td className="px-3 py-2"><DurationInput label={`${p} resolución`} value={form.policy.resolution[p]} onChange={v => setTarget('resolution', p, v)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={form.policy.p1RoundTheClock} onChange={e => set({ policy: { ...form.policy, p1RoundTheClock: e.target.checked } })} />
            P1 corre 24×7 (fines de semana y festivos incluidos)
          </label>
        </div>
        <p className="text-xs text-muted">
          Aplica a incidentes (INC) y solicitudes de servicio (SOL). Los requerimientos (REQ) no tienen SLA: siguen el flujo de
          estimación y aprobación. La primera respuesta corre sin pausa hasta que el agente contesta al cliente. La resolución se detiene mientras el ticket
          espera al cliente o un cambio programado. Al 75 % se avisa al responsable y al vencer también a los líderes del grupo.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">Compromiso por modelo de soporte</h2>
        <p className="text-sm text-muted">
          Sale del proyecto del ticket en Veta. <strong className="font-semibold text-ink">Referencia (mejor esfuerzo)</strong>: el SLA se mide y se avisa al
          responsable, pero no se escala ni cuenta como incumplimiento. <strong className="font-semibold text-ink">Comprometido</strong>: al vencer se escala a los
          líderes del grupo y aparece en "En riesgo de SLA". <strong className="font-semibold text-ink">Sin SLA</strong>: no se mide.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {(Object.keys(SUPPORT_MODEL_LABEL) as SupportModelKey[]).map(k => (
            <label key={k} className="flex items-center justify-between gap-3 rounded-lg border border-line bg-white px-3 py-2 text-sm">
              <span>{SUPPORT_MODEL_LABEL[k]}</span>
              <select className={input} value={form.commitment[k]}
                onChange={e => set({ commitment: { ...form.commitment, [k]: e.target.value as SlaCommitment } })}>
                <option value="reference">Referencia (mejor esfuerzo)</option>
                <option value="committed">Comprometido</option>
                <option value="none">Sin SLA</option>
              </select>
            </label>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">Jornada y calendario</h2>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
          <label className="inline-flex items-center gap-2">
            Lunes a viernes de
            <select aria-label="Hora de inicio" className={input} value={form.businessHours.startHour}
              onChange={e => set({ businessHours: { ...form.businessHours, startHour: Number(e.target.value) } })}>
              {hours.slice(0, 24).map(h => <option key={h} value={h}>{`${h}:00`}</option>)}
            </select>
            a
            <select aria-label="Hora de fin" className={input} value={form.businessHours.endHour}
              onChange={e => set({ businessHours: { ...form.businessHours, endHour: Number(e.target.value) } })}>
              {hours.slice(1).map(h => <option key={h} value={h}>{`${h}:00`}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-2">
            País de la empresa
            <select className={input} value={form.country} onChange={e => set({ country: e.target.value as CountryCode })}>
              {data.countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </label>
        </div>
        <p className="text-xs text-muted">
          Un día hábil = {form.businessHours.endHour - form.businessHours.startHour} h. La jornada se cuenta en la hora local del país del cliente y
          no cuentan sus festivos oficiales.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {data.countries.map(c => (
            <div key={c.code} className="rounded-lg border border-line bg-white p-3">
              <p className="text-sm font-semibold">{c.name} <span className="font-normal text-muted">· próximos festivos</span></p>
              <ul className="mt-1.5 space-y-0.5 text-xs text-[#3F3D39]">
                {c.upcoming.map(h => <li key={h.date + h.name}><span className="inline-block w-28 tabular-nums text-muted">{fmtDate(h.date)}</span>{h.name}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">País por cliente</h2>
        <p className="text-sm text-muted">Define la zona horaria y los festivos con que corre el SLA de cada cliente.</p>
        <div className="overflow-x-auto rounded-lg border border-line bg-white">
          <table className="w-full min-w-[480px] border-collapse text-sm">
            <tbody>
              {data.organizations.map(o => (
                <tr key={o.id} className="border-b border-[#EFEDE9] last:border-0">
                  <th scope="row" className="px-3 py-2 text-left font-medium">{o.name}</th>
                  <td className="px-3 py-2 text-right">
                    <select aria-label={`País de ${o.name}`} className={input} value={form.orgCountries[o.id] ?? ''}
                      onChange={e => {
                        const next = { ...form.orgCountries };
                        if (e.target.value) next[o.id] = e.target.value as CountryCode; else delete next[o.id];
                        set({ orgCountries: next });
                      }}>
                      <option value="">Igual que la empresa ({countryName(form.country)})</option>
                      {data.countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {data.organizations.length === 0 && <tr><td className="px-3 py-4 text-muted">Aún no hay clientes en Veta.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">Días de asueto adicionales</h2>
        <p className="text-sm text-muted">
          Para lo que no está en la ley o se decreta cada año: puentes turísticos, Jueves y Viernes Santo en México, cierres de fin de año.
        </p>
        {form.extra.length > 0 && (
          <ul className="flex flex-col gap-2">
            {form.extra.map((r, i) => {
              const upd = (patch: Partial<ExtraRow>) => set({ extra: form.extra.map((x, j) => j === i ? { ...x, ...patch } : x) });
              return (
                <li key={i} className="flex flex-wrap items-center gap-2">
                  <select aria-label="País" className={input} value={r.country} onChange={e => upd({ country: e.target.value as CountryCode })}>
                    {data.countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                  <input aria-label="Fecha" type="date" className={input} value={r.date} onChange={e => upd({ date: e.target.value })} />
                  <input aria-label="Motivo" className={`${input} min-w-[200px] flex-1`} placeholder="Jueves Santo" value={r.name} onChange={e => upd({ name: e.target.value })} />
                  <button type="button" className="text-sm text-muted hover:text-[#8F1D1D]" onClick={() => set({ extra: form.extra.filter((_, j) => j !== i) })}>Quitar</button>
                </li>
              );
            })}
          </ul>
        )}
        <div>
          <button type="button" className={btnSecondary} onClick={() => set({ extra: [...form.extra, { country: form.country, date: '', name: '' }] })}>+ Agregar día</button>
        </div>
      </section>

      <div className="sticky bottom-0 flex items-center gap-3 border-t border-line bg-paper py-3">
        <button type="button" className={btnPrimary} disabled={!dirty || save.isPending} onClick={() => save.mutate(form)}>
          {save.isPending ? 'Guardando…' : 'Guardar SLA'}
        </button>
        {dirty && <button type="button" className={btnSecondary} onClick={() => setForm(toForm(data))}>Descartar cambios</button>}
        {saved && !dirty && <span role="status" className="text-sm text-accent">Guardado. Los relojes de los tickets abiertos ya usan las metas nuevas.</span>}
      </div>
    </div>
  );
}
